import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, upload } from '../lib/api.ts';
import { dateTime, day as dayText, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { TONES, useAction, useSave } from './manage/common.tsx';
import { DirectoryTab } from './people/Directory.tsx';
import { ReviewsTab } from './people/Reviews.tsx';
import { ReferTab } from './people/Referrals.tsx';
import { SkillsTab } from './people/Skills.tsx';
import { downloadCsv, Kpi, plain, Table } from './Reports.tsx';

/**
 * People: time off (what is left, asking, approving, who is off), expenses paid back with their
 * receipts, and recruitment (open positions, applicants from new to hired, the careers page).
 * Everyone sees their own; managers decide, and the settings set the kinds of leave, the
 * holidays, the days given and the expense categories.
 */

type Tab = 'time' | 'team' | 'reviews' | 'skills' | 'calendar' | 'requests' | 'expenses' | 'refer' | 'recruit' | 'settings';
type LeaveState = 'requested' | 'approved' | 'refused' | 'cancelled';
type Half = 'none' | 'am' | 'pm';
type ExState = 'draft' | 'submitted' | 'approved' | 'refused' | 'paid';
type Stage = 'new' | 'screening' | 'interview' | 'offer' | 'hired' | 'refused';
const STAGES: Stage[] = ['new', 'screening', 'interview', 'offer', 'hired', 'refused'];
const SOURCES = ['website', 'referral', 'walk_in', 'job_board', 'other'] as const;
const KINDS = ['full_time', 'part_time', 'seasonal'] as const;
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

interface Overview {
  today: string;
  off_today: string[];
  leave_waiting: number | null;
  expenses: { to_approve: number; to_pay: number; to_pay_amount: number } | null;
  recruitment: { new: number; interviews: number; jobs: number } | null;
  reviews: { mine: number; team: number };
  courses: { open: number; late: number };
  referrals: { mine: number; due: number | null; due_amount: number | null };
  can: { manage: boolean; approve_leave: boolean; approve_expenses: boolean; pay_expenses: boolean; recruit: boolean };
}
interface LeaveRow {
  id: string;
  user: string;
  user_name: string;
  type: string | null;
  type_name: string | null;
  color: number;
  paid: boolean | null;
  start: string;
  end: string;
  half: Half;
  days: number;
  reason: string | null;
  state: LeaveState;
  decided_by_name: string | null;
  decided_at: string | null;
  note: string | null;
  created_at: string;
  mine: boolean;
}
interface Balance {
  type: string;
  name: string;
  color: number;
  paid: boolean;
  limited: boolean;
  half_days: boolean;
  given: number | null;
  taken: number;
  waiting: number;
  left: number | null;
}
interface MyLeave {
  year: number;
  today: string;
  balances: Balance[];
  requests: LeaveRow[];
  work_days: number[];
  next_holidays: { day: string; name: string }[];
  can_manage: boolean;
  can_approve: boolean;
}
interface LeaveType {
  id: string;
  name: string;
  paid: boolean;
  limited: boolean;
  default_days: number;
  half_days: boolean;
  color: number;
  active: boolean;
}
interface Person {
  id: string;
  name: string;
}
interface ExpenseRow {
  id: string;
  number: number;
  name: string;
  user: string;
  user_name: string;
  day: string;
  category: string;
  category_name: string;
  description: string;
  amount: number;
  state: ExState;
  note: string | null;
  over_limit: boolean;
  decision_note: string | null;
  decided_by_name: string | null;
  paid_method: 'cash' | 'bank' | null;
  paid_reference: string | null;
  paid_at: string | null;
  paid_by_name: string | null;
  receipts: number;
  mine: boolean;
}
interface Category {
  id: string;
  name: string;
  limit: number;
  active: boolean;
}

const LEAVE_STATE: Record<LeaveState, { tone: Tone; icon: IconName }> = {
  requested: { tone: 'warn', icon: 'clock' },
  approved: { tone: 'ok', icon: 'checkCircle' },
  refused: { tone: 'danger', icon: 'x' },
  cancelled: { tone: 'neutral', icon: 'undo' },
};
const EX_STATE: Record<ExState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  submitted: { tone: 'warn', icon: 'clock' },
  approved: { tone: 'info', icon: 'check' },
  refused: { tone: 'danger', icon: 'x' },
  paid: { tone: 'ok', icon: 'checkCircle' },
};
const tone = (n: number) => `tone-${TONES[n % TONES.length]}`;
const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const weekday = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();
const daysText = (n: number) => (Number.isInteger(n) ? tn('hr.n_days', n) : t('hr.n_days_half', { n: String(n) }));
const range = (l: { start: string; end: string; half: Half }) =>
  l.start === l.end ? `${dayText(l.start)}${l.half !== 'none' ? ` · ${t(`hr.half_${l.half}` as MessageKey)}` : ''}` : `${dayText(l.start)} – ${dayText(l.end)}`;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
const weekdayName = (n: number) => new Date(Date.UTC(2024, 0, 7 + n)).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });

const readBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });
const ACCEPT = 'application/pdf,image/jpeg,image/png';
const MAX_FILE = 8_000_000;
function openFile(f: { name: string; mime: string; data: string }) {
  const bytes = Uint8Array.from(atob(f.data), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: f.mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = f.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
const size = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);

export function People() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const { data: o, reload } = useAction<Overview>('hr.overview');
  const tabs: { key: Tab; icon: IconName; show: boolean; badge?: number }[] = [
    { key: 'time', icon: 'sun', show: true },
    { key: 'team', icon: 'contact', show: true },
    { key: 'reviews', icon: 'star', show: true, badge: (o?.reviews.mine ?? 0) + (o?.reviews.team ?? 0) },
    { key: 'skills', icon: 'book', show: true, badge: o?.courses.open ?? 0 },
    { key: 'calendar', icon: 'calendar', show: true },
    { key: 'requests', icon: 'checkCircle', show: can('hr.leave_approve'), badge: o?.leave_waiting ?? 0 },
    { key: 'expenses', icon: 'receipt', show: true, badge: (can('hr.expense_approve') ? (o?.expenses?.to_approve ?? 0) : 0) + (can('hr.expense_pay') ? (o?.expenses?.to_pay ?? 0) : 0) },
    { key: 'refer', icon: 'gift', show: true, badge: o?.referrals.due ?? 0 },
    { key: 'recruit', icon: 'users', show: can('hr.recruit'), badge: o?.recruitment?.new ?? 0 },
    { key: 'settings', icon: 'settings', show: can('hr.manage') },
  ];
  const visible = tabs.filter((x) => x.show);
  const tab = (visible.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'time') as Tab;
  const go = (key: Tab) => {
    const p = new URLSearchParams();
    if (key !== 'time') p.set('tab', key);
    setParams(p, { replace: true });
  };
  const [ask, setAsk] = useState(false);
  const [version, setVersion] = useState(0);
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  return (
    <Shell>
      <div className="page reports hr-page">
        <div className="page-head">
          <div>
            <h1>{t('hr.title')}</h1>
            <p className="muted">{t('hr.subtitle')}</p>
          </div>
          <div className="page-actions">
            <Button variant="primary" icon="plus" onClick={() => setAsk(true)}>
              {t('hr.ask_time_off')}
            </Button>
          </div>
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('hr.at_a_glance')}>
            <Kpi label={t('hr.k_off_today')} value={String(o.off_today.length)} hint={o.off_today.length ? o.off_today.join(', ') : t('hr.k_everyone_in')} />
            {o.leave_waiting !== null ? <Kpi label={t('hr.k_leave_waiting')} value={String(o.leave_waiting)} hint={t('hr.k_leave_waiting_hint')} warn={o.leave_waiting > 0} /> : null}
            {o.expenses ? (
              <Kpi
                label={t('hr.k_expenses')}
                value={money(o.expenses.to_pay_amount)}
                hint={t('hr.k_expenses_hint', { approve: o.expenses.to_approve, pay: o.expenses.to_pay })}
                warn={o.expenses.to_approve > 0}
              />
            ) : null}
            {o.recruitment ? (
              <Kpi label={t('hr.k_recruit')} value={String(o.recruitment.new)} hint={t('hr.k_recruit_hint', { jobs: o.recruitment.jobs, interviews: o.recruitment.interviews })} />
            ) : null}
          </section>
        )}
        <div className="segmented tabs" role="tablist" aria-label={t('hr.sections')}>
          {visible.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => go(x.key)}>
              <Icon name={x.icon} size={16} />
              {t(`hr.tab_${x.key}` as MessageKey)}
              {x.badge ? <span className="hr-tab-badge">{x.badge}</span> : null}
            </button>
          ))}
        </div>
        {tab === 'time' ? (
          <TimeTab key={version} onAsk={() => setAsk(true)} onChange={changed} />
        ) : tab === 'team' ? (
          <DirectoryTab />
        ) : tab === 'reviews' ? (
          <ReviewsTab onChange={changed} />
        ) : tab === 'skills' ? (
          <SkillsTab onChange={changed} />
        ) : tab === 'calendar' ? (
          <CalendarTab key={version} />
        ) : tab === 'requests' ? (
          <RequestsTab key={version} onChange={changed} />
        ) : tab === 'expenses' ? (
          <ExpensesTab key={version} onChange={changed} />
        ) : tab === 'refer' ? (
          <ReferTab onChange={changed} />
        ) : tab === 'recruit' ? (
          <RecruitTab onChange={changed} />
        ) : (
          <SettingsTab />
        )}
      </div>
      {ask ? (
        <LeaveDialog
          onClose={() => setAsk(false)}
          onSaved={async () => {
            setAsk(false);
            await changed();
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- time off ---------------------------------------------------------------------------

function Meter({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <span className="tk-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
      <span style={{ width: `${pct}%` }} />
    </span>
  );
}

function TimeTab({ onAsk, onChange }: { onAsk: () => void; onChange: () => Promise<void> }) {
  const { data } = useAction<MyLeave>('hr.my_leave');
  const [cancel, setCancel] = useState<LeaveRow | null>(null);
  if (!data) return <Skeleton h={300} />;
  const upcoming = data.requests.filter((r) => r.end >= data.today && (r.state === 'requested' || r.state === 'approved'));
  const past = data.requests.filter((r) => !upcoming.includes(r));
  return (
    <>
      {data.balances.length ? (
        <ul className="hr-balances" aria-label={t('hr.balances')}>
          {data.balances.map((b) => (
            <li key={b.type} className="hr-balance">
              <div className="hr-balance-head">
                <span className={`hr-dot ${tone(b.color)}`} aria-hidden="true" />
                <strong>{b.name}</strong>
                {!b.paid ? <span className="muted small">{t('hr.unpaid')}</span> : null}
              </div>
              {b.limited ? (
                <>
                  <span className="hr-balance-value">
                    {b.left ?? 0}
                    <small>{t('hr.of_days_left', { n: b.given ?? 0 })}</small>
                  </span>
                  <Meter value={b.taken + b.waiting} max={b.given ?? 0} label={t('hr.used_of', { used: b.taken + b.waiting, given: b.given ?? 0 })} />
                  <span className="muted small">
                    {t('hr.taken_waiting', { taken: b.taken, waiting: b.waiting })}
                    {b.given === 0 ? ` · ${t('hr.none_given')}` : ''}
                  </span>
                </>
              ) : (
                <>
                  <span className="hr-balance-value">
                    {b.taken}
                    <small>{t('hr.days_taken_year', { year: data.year })}</small>
                  </span>
                  <span className="muted small">{t('hr.no_limit')}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="sun" title={t('hr.no_types')} body={data.can_manage ? t('hr.no_types_manager') : t('hr.no_types_staff')} />
      )}
      <section className="panel">
        <div className="panel-head">
          <h2>{t('hr.my_requests')}</h2>
          <Button size="sm" icon="plus" onClick={onAsk} disabled={!data.balances.length}>
            {t('hr.ask_time_off')}
          </Button>
        </div>
        {upcoming.length ? <LeaveList list={upcoming} onCancel={setCancel} /> : <p className="muted">{t('hr.nothing_planned')}</p>}
        {past.length ? (
          <details className="tk-entries">
            <summary>{tn('hr.earlier', past.length)}</summary>
            <LeaveList list={past} />
          </details>
        ) : null}
      </section>
      {data.next_holidays.length ? (
        <section className="panel">
          <h2>{t('hr.next_holidays')}</h2>
          <ul className="hr-holidays">
            {data.next_holidays.map((h) => (
              <li key={h.day}>
                <Icon name="star" size={16} />
                <strong>{dayText(h.day)}</strong>
                <span className="muted">{h.name}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {cancel ? (
        <ReasonDialog
          title={t('hr.cancel_title')}
          body={`${cancel.type_name ?? ''} · ${range(cancel)}`}
          need={cancel.state === 'approved'}
          label={t('hr.cancel_reason')}
          confirm={t('hr.cancel_request')}
          onClose={() => setCancel(null)}
          onConfirm={async (reason) => {
            await action('hr.cancel_leave', { id: cancel.id, reason });
            setCancel(null);
            await onChange();
          }}
          done={t('hr.cancelled_toast')}
        />
      ) : null}
    </>
  );
}

function LeaveList({ list, onCancel }: { list: LeaveRow[]; onCancel?: (l: LeaveRow) => void }) {
  return (
    <ul className="hr-leaves">
      {list.map((l) => (
        <li key={l.id}>
          <span className={`hr-dot ${tone(l.color)}`} aria-hidden="true" />
          <div className="hr-leave-main">
            <strong>{range(l)}</strong>
            <span className="muted small">
              {[l.type_name, daysText(l.days), l.reason].filter(Boolean).join(' · ')}
              {l.note && (l.state === 'refused' || l.state === 'cancelled') ? ` · “${l.note}”` : ''}
            </span>
          </div>
          <StatusBadge tone={LEAVE_STATE[l.state].tone} icon={LEAVE_STATE[l.state].icon} label={t(`hr.ls_${l.state}` as MessageKey)} />
          {onCancel && (l.state === 'requested' || l.state === 'approved') ? <IconButton icon="x" label={t('hr.cancel_request')} variant="ghost" onClick={() => onCancel(l)} /> : null}
        </li>
      ))}
    </ul>
  );
}

type Preview = { ok: boolean; days: number; left: number | null; enough: boolean; problem: string | null };
/** How many days the request takes and what is left after it, worked out by the server as the form changes. */
function usePreview(input: Record<string, unknown> | null) {
  const [data, setData] = useState<Preview | null>(null);
  const key = input ? JSON.stringify(input) : null;
  useEffect(() => {
    if (!key) return setData(null);
    let live = true;
    const timer = setTimeout(() => {
      action<Preview>('hr.leave_preview', JSON.parse(key)).then(
        (r) => live && setData(r),
        () => live && setData(null),
      );
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [key]);
  return data;
}

interface LeaveForm {
  type: string;
  start: string;
  end: string;
  half: Half;
  reason: string;
  user: string;
  approve: boolean;
}

function LeaveDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { can, me } = useSession();
  const { data: mine } = useAction<MyLeave>('hr.my_leave');
  const manager = can('hr.manage');
  const { data: cal } = useAction<{ people: Person[] }>('hr.calendar', { month: todayIso().slice(0, 7) });
  const { busy, run } = useSave();
  const start = addDays(todayIso(), 1);
  const [f, setF] = useState<LeaveForm>({ type: '', start, end: start, half: 'none', reason: '', user: '', approve: false });
  const set = <K extends keyof LeaveForm>(k: K, v: LeaveForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const type = f.type || mine?.balances[0]?.type || '';
  const kind = mine?.balances.find((b) => b.type === type);
  const forOther = !!f.user && f.user !== me?.userId;
  const input = { type, start: f.start, end: f.half !== 'none' ? f.start : f.end, half: f.half, reason: f.reason.trim() || null, user: f.user || null };
  const preview = usePreview(type ? input : null);
  const list = cal?.people;
  return (
    <Dialog
      title={t('hr.ask_time_off')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="send"
            busy={busy}
            disabled={!preview?.ok || !preview.enough}
            onClick={() =>
              void run(
                async () => {
                  await action('hr.request_leave', { ...input, approve: forOther && f.approve });
                  await onSaved();
                },
                forOther && f.approve ? t('hr.entered_toast') : t('hr.asked_toast'),
              )
            }
          >
            {forOther ? (f.approve ? t('hr.enter_approved') : t('hr.enter_request')) : t('hr.send_request')}
          </Button>
        </span>
      }
    >
      {!mine ? (
        <Skeleton h={240} />
      ) : !mine.balances.length ? (
        <EmptyState icon="sun" title={t('hr.no_types')} body={mine.can_manage ? t('hr.no_types_manager') : t('hr.no_types_staff')} />
      ) : (
        <div className="form-stack">
          {manager && list ? (
            <Field label={t('hr.for_whom')}>
              <select className="input" value={f.user} onChange={(e) => set('user', e.target.value)}>
                <option value="">{t('hr.myself')}</option>
                {list
                  .filter((p) => p.id !== me?.userId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
            </Field>
          ) : null}
          <fieldset className="hr-kinds">
            <legend>{t('hr.kind')}</legend>
            {mine.balances.map((b) => (
              <button
                type="button"
                key={b.type}
                className={`option-card${type === b.type ? ' selected' : ''}`}
                aria-pressed={type === b.type}
                onClick={() => setF((x) => ({ ...x, type: b.type, half: b.half_days ? x.half : 'none' }))}
              >
                <span className={`hr-dot ${tone(b.color)}`} aria-hidden="true" />
                <strong>{b.name}</strong>
                {!forOther ? <span className="muted small">{b.limited ? t('hr.n_left', { n: b.left ?? 0 }) : t('hr.no_limit')}</span> : null}
              </button>
            ))}
          </fieldset>
          <div className="form-grid">
            <Field label={t('hr.first_day')}>
              <input
                className="input"
                type="date"
                value={f.start}
                onChange={(e) => e.target.value && setF((x) => ({ ...x, start: e.target.value, end: x.end < e.target.value ? e.target.value : x.end }))}
              />
            </Field>
            <Field label={t('hr.last_day')}>
              <input
                className="input"
                type="date"
                value={f.half !== 'none' ? f.start : f.end}
                min={f.start}
                disabled={f.half !== 'none'}
                onChange={(e) => e.target.value && set('end', e.target.value)}
              />
            </Field>
          </div>
          {kind?.half_days ? (
            <div className="hr-halves" role="radiogroup" aria-label={t('hr.how_long')}>
              {(['none', 'am', 'pm'] as Half[]).map((h) => (
                <button type="button" key={h} role="radio" aria-checked={f.half === h} className={`chip${f.half === h ? ' active' : ''}`} onClick={() => set('half', h)}>
                  {t(`hr.length_${h}` as MessageKey)}
                </button>
              ))}
            </div>
          ) : null}
          <div className={`hr-preview${preview && (!preview.ok || !preview.enough) ? ' bad' : ''}`} aria-live="polite">
            {!preview ? (
              <span className="muted">…</span>
            ) : !preview.ok ? (
              <>
                <Icon name="alert" size={18} /> {preview.problem}
              </>
            ) : (
              <>
                <Icon name={preview.enough ? 'calendar' : 'alert'} size={18} />
                <span>
                  <strong>{daysText(preview.days)}</strong>
                  {preview.left !== null ? ` · ${preview.enough ? t('hr.left_after', { n: preview.left }) : t('hr.not_enough', { n: preview.left + preview.days })}` : ''}
                </span>
              </>
            )}
          </div>
          <Field label={t('hr.reason')} hint={t('hr.reason_hint')}>
            <textarea className="input" rows={2} maxLength={500} value={f.reason} onChange={(e) => set('reason', e.target.value)} />
          </Field>
          {forOther && can('hr.leave_approve') ? (
            <div className="setting-row">
              <div>
                <strong>{t('hr.approve_now')}</strong>
                <p className="muted">{t('hr.approve_now_hint')}</p>
              </div>
              <Switch checked={f.approve} onChange={(v) => set('approve', v)} label={t('hr.approve_now')} />
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function ReasonDialog({
  title,
  body,
  need,
  label,
  confirm,
  danger = true,
  done,
  onClose,
  onConfirm,
}: {
  title: string;
  body?: string;
  need: boolean;
  label: string;
  confirm: string;
  danger?: boolean;
  done: string;
  onClose: () => void;
  onConfirm: (reason: string | null) => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('hr.back')}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            busy={busy}
            onClick={() => {
              setTried(true);
              if (need && reason.trim().length < 3) return;
              void run(() => onConfirm(reason.trim() || null), done);
            }}
          >
            {confirm}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        {body ? <p>{body}</p> : null}
        <Field label={label} hint={need ? undefined : t('hr.optional')} error={tried && need && reason.trim().length < 3 ? t('hr.e_reason') : null} required={need}>
          <textarea className="input" rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

interface Request extends LeaveRow {
  left_before: number | null;
  left_after: number | null;
  also_off: string[];
  shifts: number;
  own: boolean;
}

function RequestsTab({ onChange }: { onChange: () => Promise<void> }) {
  const [state, setState] = useState<'requested' | 'decided'>('requested');
  const { data, reload } = useAction<{ requests: Request[]; decide_own: boolean }>('hr.leave_requests', { state });
  const { busy, run } = useSave();
  const [refuse, setRefuse] = useState<Request | null>(null);
  const done = async () => {
    await reload();
    await onChange();
  };
  return (
    <section className="panel">
      <div className="panel-head">
        <div className="segmented" role="tablist" aria-label={t('hr.requests_show')}>
          {(['requested', 'decided'] as const).map((s) => (
            <button key={s} role="tab" aria-selected={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
              {t(`hr.req_${s}` as MessageKey)}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : !data.requests.length ? (
        <EmptyState icon="checkCircle" title={state === 'requested' ? t('hr.no_requests') : t('hr.no_decided')} body={state === 'requested' ? t('hr.no_requests_hint') : undefined} />
      ) : (
        <ul className="hr-requests">
          {data.requests.map((r) => {
            const mayDecide = r.state === 'requested' && (!r.own || data.decide_own);
            return (
              <li key={r.id} className="hr-request">
                <span className="tk-avatar" aria-hidden="true">
                  {initials(r.user_name)}
                </span>
                <div className="hr-request-main">
                  <div className="hr-request-title">
                    <strong>{r.user_name}</strong>
                    <span className={`hr-type ${tone(r.color)}`}>{r.type_name}</span>
                    {r.state !== 'requested' ? <StatusBadge tone={LEAVE_STATE[r.state].tone} icon={LEAVE_STATE[r.state].icon} label={t(`hr.ls_${r.state}` as MessageKey)} /> : null}
                  </div>
                  <span>
                    <strong>{range(r)}</strong> · {daysText(r.days)}
                  </span>
                  {r.reason ? <span className="muted small">“{r.reason}”</span> : null}
                  <div className="hr-facts small">
                    {r.left_before !== null ? (
                      <span className={r.left_after !== null && r.left_after < 0 ? 'tk-warn' : 'muted'}>
                        <Icon name="sun" size={14} /> {t('hr.left_then', { before: r.left_before, after: r.left_after ?? 0 })}
                      </span>
                    ) : null}
                    {r.also_off.length ? (
                      <span className="tk-warn">
                        <Icon name="users" size={14} /> {t('hr.also_off', { names: r.also_off.join(', ') })}
                      </span>
                    ) : state === 'requested' ? (
                      <span className="muted">
                        <Icon name="users" size={14} /> {t('hr.nobody_else_off')}
                      </span>
                    ) : null}
                    {r.shifts ? (
                      <span className="tk-warn">
                        <Icon name="clock" size={14} /> {tn('hr.planned_shifts', r.shifts)}
                      </span>
                    ) : null}
                    {r.state !== 'requested' && r.decided_by_name ? (
                      <span className="muted">
                        {t('hr.decided_by', { name: r.decided_by_name, at: r.decided_at ? dateTime(r.decided_at) : '' })}
                        {r.note ? ` · “${r.note}”` : ''}
                      </span>
                    ) : null}
                  </div>
                  {r.own && r.state === 'requested' && !data.decide_own ? <span className="muted small">{t('hr.own_other_decides')}</span> : null}
                </div>
                {mayDecide ? (
                  <div className="hr-request-actions">
                    <Button size="sm" variant="ghost" icon="x" onClick={() => setRefuse(r)}>
                      {t('hr.refuse')}
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      icon="check"
                      busy={busy}
                      disabled={r.left_after !== null && r.left_after < 0}
                      onClick={() =>
                        void run(
                          async () => {
                            await action('hr.decide_leave', { id: r.id, approve: true });
                            await done();
                          },
                          t('hr.approved_toast', { name: r.user_name }),
                        )
                      }
                    >
                      {t('hr.approve')}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {refuse ? (
        <ReasonDialog
          title={t('hr.refuse_title', { name: refuse.user_name })}
          body={`${refuse.type_name ?? ''} · ${range(refuse)}`}
          need
          label={t('hr.refuse_reason')}
          confirm={t('hr.refuse')}
          done={t('hr.refused_toast')}
          onClose={() => setRefuse(null)}
          onConfirm={async (note) => {
            await action('hr.decide_leave', { id: refuse.id, approve: false, note });
            setRefuse(null);
            await done();
          }}
        />
      ) : null}
    </section>
  );
}

interface Calendar {
  month: string;
  first: string;
  last: string;
  today: string;
  work_days: number[];
  holidays: { day: string; name: string }[];
  people: Person[];
  leaves: LeaveRow[];
}

function CalendarTab() {
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const { data } = useAction<Calendar>('hr.calendar', { month });
  const shift = (n: number) => {
    const [y, m] = month.split('-').map(Number) as [number, number];
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  const [onlyOff, setOnlyOff] = useState(true);
  const days = useMemo(() => {
    if (!data) return [];
    const out: string[] = [];
    for (let d = data.first; d <= data.last; d = addDays(d, 1)) out.push(d);
    return out;
  }, [data]);
  const hol = new Map((data?.holidays ?? []).map((h) => [h.day, h.name]));
  const off = (person: string, d: string) => data?.leaves.find((l) => l.user === person && l.start <= d && l.end >= d);
  const rows = (data?.people ?? []).filter((p) => !onlyOff || data!.leaves.some((l) => l.user === p.id));
  const title = new Date(`${month}-15T12:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return (
    <section className="panel">
      <div className="panel-head hr-cal-head">
        <div className="hr-month">
          <IconButton icon="back" label={t('hr.prev_month')} variant="ghost" onClick={() => shift(-1)} />
          <h2>{title}</h2>
          <IconButton icon="chevronRight" label={t('hr.next_month')} variant="ghost" onClick={() => shift(1)} />
          {month !== todayIso().slice(0, 7) ? (
            <Button size="sm" variant="ghost" onClick={() => setMonth(todayIso().slice(0, 7))}>
              {t('hr.this_month')}
            </Button>
          ) : null}
        </div>
        <label className="hr-check">
          <input type="checkbox" checked={onlyOff} onChange={(e) => setOnlyOff(e.target.checked)} /> {t('hr.only_off')}
        </label>
      </div>
      {!data ? (
        <Skeleton h={260} />
      ) : (
        <>
          <div className="hr-cal-wrap" tabIndex={0} aria-label={t('hr.calendar_scroll')}>
            <table className="hr-cal">
              <thead>
                <tr>
                  <th scope="col" className="hr-cal-name">
                    {t('hr.person')}
                  </th>
                  {days.map((d) => (
                    <th
                      key={d}
                      scope="col"
                      className={[!data.work_days.includes(weekday(d)) ? 'rest' : '', hol.has(d) ? 'holiday' : '', d === data.today ? 'today' : ''].filter(Boolean).join(' ') || undefined}
                      title={hol.get(d) ?? undefined}
                    >
                      <span className="hr-cal-wd">{weekdayName(weekday(d)).slice(0, 2)}</span>
                      <span>{Number(d.slice(8))}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((p) => (
                    <tr key={p.id}>
                      <th scope="row" className="hr-cal-name">
                        {p.name}
                      </th>
                      {days.map((d) => {
                        const l = off(p.id, d);
                        const rest = !data.work_days.includes(weekday(d)) || hol.has(d);
                        return (
                          <td key={d} className={[rest ? 'rest' : '', d === data.today ? 'today' : ''].filter(Boolean).join(' ') || undefined}>
                            {l && !rest ? (
                              <span
                                className={`hr-cal-off ${tone(l.color)}${l.state === 'requested' ? ' asked' : ''}${l.half !== 'none' ? ' half' : ''}`}
                                title={`${p.name}: ${l.type_name ?? t('hr.off')} · ${t(`hr.ls_${l.state}` as MessageKey)}`}
                              >
                                <span className="sr-only">{l.type_name ?? t('hr.off')}</span>
                              </span>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={days.length + 1} className="muted hr-cal-empty">
                      {t('hr.nobody_off_month')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="hr-legend small muted">
            <span>
              <span className="hr-cal-off tone-blue" /> {t('hr.ls_approved')}
            </span>
            <span>
              <span className="hr-cal-off tone-blue asked" /> {t('hr.ls_requested')}
            </span>
            <span>
              <span className="hr-legend-rest" /> {t('hr.rest_days')}
            </span>
          </div>
          {data.holidays.length ? (
            <p className="small muted">
              <Icon name="star" size={14} /> {data.holidays.map((h) => `${dayText(h.day)} ${h.name}`).join(' · ')}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

// ---- expenses ---------------------------------------------------------------------------

type ExFilter = 'mine' | 'to_approve' | 'to_pay' | 'paid' | 'all';
interface ExpenseList {
  expenses: ExpenseRow[];
  totals: { to_approve: number; n_approve: number; to_pay: number; n_pay: number; my_draft: number; my_owed: number };
  categories: Category[];
  can_approve: boolean;
  can_pay: boolean;
  can_manage: boolean;
}

function ExpensesTab({ onChange }: { onChange: () => Promise<void> }) {
  const { can } = useSession();
  const filters: { key: ExFilter; show: boolean }[] = [
    { key: 'mine', show: true },
    { key: 'to_approve', show: can('hr.expense_approve') },
    { key: 'to_pay', show: can('hr.expense_pay') },
    { key: 'paid', show: can('hr.expense_pay') },
    { key: 'all', show: can('hr.expense_approve') || can('hr.expense_pay') },
  ];
  const [filter, setFilter] = useState<ExFilter>('mine');
  const [search, setSearch] = useState('');
  const { data, reload } = useAction<ExpenseList>('hr.expenses', { filter, search: search.trim() || null });
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState<ExpenseRow | 'new' | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pay, setPay] = useState(false);
  const { busy, run } = useSave();
  const done = async () => {
    setPicked(new Set());
    await reload();
    await onChange();
  };
  const drafts = (data?.expenses ?? []).filter((e) => e.mine && e.state === 'draft');
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const selectable = filter === 'to_pay';
  const pickedTotal = (data?.expenses ?? []).filter((e) => picked.has(e.id)).reduce((s, e) => s + e.amount, 0);
  const csv = () =>
    downloadCsv(
      `expenses-${todayIso()}.csv`,
      [t('hr.number'), t('hr.day'), t('hr.person'), t('hr.category'), t('hr.description'), t('hr.amount'), t('hr.state'), t('hr.paid_how'), t('hr.paid_ref')],
      (data?.expenses ?? []).map((e) => [
        e.name,
        e.day,
        e.user_name,
        e.category_name,
        e.description,
        plain(e.amount),
        t(`hr.es_${e.state}` as MessageKey),
        e.paid_method ? t(`hr.pay_${e.paid_method}` as MessageKey) : '',
        e.paid_reference ?? '',
      ]),
    );
  return (
    <>
      {data ? (
        <div className="hr-ex-summary">
          {filter === 'mine' ? (
            <>
              <span>
                <span className="muted small">{t('hr.my_owed')}</span>
                <strong>{money(data.totals.my_owed)}</strong>
              </span>
              <span>
                <span className="muted small">{t('hr.my_draft')}</span>
                <strong>{money(data.totals.my_draft)}</strong>
              </span>
            </>
          ) : (
            <>
              <span>
                <span className="muted small">{tn('hr.to_approve_n', data.totals.n_approve)}</span>
                <strong>{money(data.totals.to_approve)}</strong>
              </span>
              <span>
                <span className="muted small">{tn('hr.to_pay_n', data.totals.n_pay)}</span>
                <strong>{money(data.totals.to_pay)}</strong>
              </span>
            </>
          )}
        </div>
      ) : null}
      <section className="panel">
        <div className="panel-head hr-ex-head">
          {filters.filter((x) => x.show).length > 1 ? (
            <div className="segmented" role="tablist" aria-label={t('hr.expenses_show')}>
              {filters
                .filter((x) => x.show)
                .map((x) => (
                  <button
                    key={x.key}
                    role="tab"
                    aria-selected={filter === x.key}
                    className={filter === x.key ? 'active' : ''}
                    onClick={() => {
                      setFilter(x.key);
                      setPicked(new Set());
                    }}
                  >
                    {t(`hr.ef_${x.key}` as MessageKey)}
                  </button>
                ))}
            </div>
          ) : (
            <h2>{t('hr.my_expenses')}</h2>
          )}
          <div className="hr-ex-tools">
            {filter !== 'mine' ? (
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" placeholder={t('hr.search_expenses')} value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
            ) : null}
            <Button size="sm" icon="download" variant="ghost" onClick={csv} disabled={!data?.expenses.length}>
              {t('hr.csv')}
            </Button>
            {filter === 'mine' && drafts.length ? (
              <Button
                size="sm"
                icon="send"
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action('hr.submit_expenses', { ids: drafts.map((d) => d.id) });
                      await done();
                    },
                    tn('hr.sent_n', drafts.length),
                  )
                }
              >
                {tn('hr.send_drafts', drafts.length)}
              </Button>
            ) : null}
            <Button size="sm" variant="primary" icon="plus" onClick={() => setForm('new')} disabled={!data?.categories.length}>
              {t('hr.new_expense')}
            </Button>
          </div>
        </div>
        {data && !data.categories.length ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} /> {data.can_manage ? t('hr.no_categories_manager') : t('hr.no_categories')}
          </p>
        ) : null}
        {!data ? (
          <Skeleton h={240} />
        ) : (
          <Table
            view="people.expenses"
            head={[...(selectable ? [''] : []), t('hr.number'), t('hr.day'), ...(filter === 'mine' ? [] : [t('hr.person')]), t('hr.description'), t('hr.amount'), t('hr.state')]}
            left={selectable ? [3, 4] : filter === 'mine' ? [2] : [2, 3]}
            phoneHide={selectable ? [1, 2, 6] : filter === 'mine' ? [0, 1] : filter === 'all' ? [0, 1, 2] : [0, 1, 5]}
            empty={filter === 'mine' ? t('hr.no_expenses_mine') : t('hr.no_expenses')}
            rows={data.expenses.map((e) => ({
              key: e.id,
              onClick: () => setOpen(e.id),
              cells: [
                ...(selectable
                  ? [<input key="pick" type="checkbox" aria-label={t('hr.pick', { name: e.name })} checked={picked.has(e.id)} onClick={(ev) => ev.stopPropagation()} onChange={() => toggle(e.id)} />]
                  : []),
                e.name,
                dayText(e.day),
                ...(filter === 'mine' ? [] : [e.user_name]),
                <span key="d" className="hr-ex-desc">
                  <strong>{e.description}</strong>
                  <span className="muted small">
                    {e.category_name}
                    {e.receipts ? '' : ` · ${t('hr.no_receipt')}`}
                    {e.over_limit ? ` · ${t('hr.over_limit')}` : ''}
                  </span>
                </span>,
                money(e.amount),
                <StatusBadge key="s" tone={EX_STATE[e.state].tone} icon={EX_STATE[e.state].icon} label={t(`hr.es_${e.state}` as MessageKey)} />,
              ],
            }))}
          />
        )}
        {selectable && data?.expenses.length ? (
          <div className="hr-paybar">
            <span>
              {picked.size ? (
                <>
                  {tn('hr.picked_n', picked.size)} · <strong>{money(pickedTotal)}</strong>
                </>
              ) : (
                <span className="muted">{t('hr.pick_hint')}</span>
              )}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setPicked(picked.size === data.expenses.length ? new Set() : new Set(data.expenses.map((e) => e.id)))}>
              {picked.size === data.expenses.length ? t('hr.pick_none') : t('hr.pick_all')}
            </Button>
            <Button size="sm" variant="primary" icon="cash" disabled={!picked.size} onClick={() => setPay(true)}>
              {t('hr.pay_back')}
            </Button>
          </div>
        ) : null}
      </section>
      {open ? (
        <ExpenseDialog
          id={open}
          onClose={() => setOpen(null)}
          onEdit={(e) => {
            setOpen(null);
            setForm(e);
          }}
          onChange={done}
        />
      ) : null}
      {form && data ? (
        <ExpenseForm
          initial={form === 'new' ? null : form}
          categories={data.categories}
          onClose={() => setForm(null)}
          onSaved={async (id) => {
            setForm(null);
            await done();
            setOpen(id);
          }}
        />
      ) : null}
      {pay ? (
        <PayDialog
          count={picked.size}
          total={pickedTotal}
          onClose={() => setPay(false)}
          onPay={async (method, reference) => {
            await action('hr.pay_expenses', { ids: [...picked], method, reference });
            setPay(false);
            await done();
          }}
        />
      ) : null}
    </>
  );
}

function ExpenseForm({ initial, categories, onClose, onSaved }: { initial: ExpenseRow | null; categories: Category[]; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    day: initial?.day ?? todayIso(),
    category: initial?.category ?? categories[0]?.id ?? '',
    description: initial?.description ?? '',
    amount: initial ? plain(initial.amount) : '',
    note: initial?.note ?? '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const { can } = useSession();
  // What the person typed themselves is never overwritten by the reading of the receipt.
  const touched = useRef(new Set<keyof typeof f>());
  const [reading, setReading] = useState<'idle' | 'busy' | 'filled' | 'unreadable'>('idle');
  const [foreign, setForeign] = useState<{ total: number; currency: string } | null>(null);
  const pick = async (picked: File | null) => {
    setFile(picked);
    setForeign(null);
    setReading('idle');
    if (!picked || picked.size > MAX_FILE || initial || !can('ai.read_receipts')) return;
    setReading('busy');
    try {
      const r = await upload<ReceiptReading>('ai.read_receipt', { name: picked.name, data: await readBase64(picked) });
      if (!r.ready) return setReading('idle');
      if (!r.readable) return setReading('unreadable');
      setF((x) => {
        const free = (k: keyof typeof f) => !touched.current.has(k) || !String(x[k]).trim();
        const description = r.description && r.merchant && !r.description.toLowerCase().includes(r.merchant.toLowerCase()) ? `${r.description} · ${r.merchant}` : (r.description ?? r.merchant);
        return {
          ...x,
          ...(description && free('description') ? { description: description.slice(0, 200) } : {}),
          ...(r.amount && free('amount') ? { amount: plain(r.amount) } : {}),
          ...(r.day && !touched.current.has('day') ? { day: r.day } : {}),
          ...(r.category && !touched.current.has('category') && categories.some((c) => c.id === r.category) ? { category: r.category } : {}),
        };
      });
      setForeign(r.foreign);
      setReading('filled');
    } catch {
      // Reading is a help: the form still works by hand.
      setReading('idle');
    }
  };
  const amount = parseMoney(f.amount);
  const cat = categories.find((c) => c.id === f.category);
  const over = !!cat && cat.limit > 0 && amount !== null && amount > cat.limit;
  const ok = f.description.trim().length >= 3 && amount !== null && amount > 0 && !!f.category && (!file || file.size <= MAX_FILE);
  const set = (k: keyof typeof f, v: string) => {
    touched.current.add(k);
    setF((x) => ({ ...x, [k]: v }));
  };
  return (
    <Dialog
      title={initial ? t('hr.edit_expense', { name: initial.name }) : t('hr.new_expense')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!ok) return;
              void run(async () => {
                const e = await action<ExpenseRow>('hr.save_expense', {
                  id: initial?.id ?? null,
                  day: f.day,
                  category: f.category,
                  description: f.description.trim(),
                  amount,
                  note: f.note.trim() || null,
                });
                if (file) await upload('hr.add_receipt', { id: e.id, name: file.name, data: await readBase64(file) });
                await onSaved(e.id);
              }, t('hr.expense_saved'));
            }}
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        {!initial ? (
          <div className="hr-receipt-pick">
            <input ref={input} type="file" accept={ACCEPT} capture="environment" hidden aria-label={t('hr.add_receipt_photo')} onChange={(e) => void pick(e.target.files?.[0] ?? null)} />
            <Button icon={file ? 'checkCircle' : 'camera'} variant={file ? 'secondary' : 'ghost'} busy={reading === 'busy'} onClick={() => input.current?.click()}>
              {file ? file.name : t('hr.add_receipt_photo')}
            </Button>
            <span className={`small${file && file.size > MAX_FILE ? ' tk-warn' : ' muted'}`}>
              {file && file.size > MAX_FILE ? t('hr.file_too_big') : reading === 'busy' ? t('hr.reading_receipt') : can('ai.read_receipts') && !file ? t('hr.receipt_hint_ai') : t('hr.receipt_hint')}
            </span>
          </div>
        ) : null}
        {reading === 'filled' ? (
          <p className="callout callout-info hr-read-note" role="status">
            <Icon name="sparkle" size={16} />
            <span>{foreign ? t('hr.read_foreign', { total: inCurrency(foreign.total, foreign.currency) }) : t('hr.read_filled')}</span>
          </p>
        ) : reading === 'unreadable' ? (
          <p className="callout callout-warn hr-read-note" role="status">
            <Icon name="alert" size={16} />
            <span>{t('hr.read_unreadable')}</span>
          </p>
        ) : null}
        <Field label={t('hr.description')} error={tried && f.description.trim().length < 3 ? t('hr.e_description') : null} required>
          <input className="input" value={f.description} maxLength={200} placeholder={t('hr.description_ph')} onChange={(e) => set('description', e.target.value)} data-autofocus />
        </Field>
        <div className="form-grid">
          <Field label={t('hr.amount')} error={tried && !(amount && amount > 0) ? t('hr.e_amount') : null} required>
            <input className="input" inputMode="decimal" value={f.amount} placeholder="0.00" onChange={(e) => set('amount', e.target.value)} />
          </Field>
          <Field label={t('hr.day')}>
            <input className="input" type="date" value={f.day} max={todayIso()} onChange={(e) => e.target.value && set('day', e.target.value)} />
          </Field>
        </div>
        <Field label={t('hr.category')} hint={cat?.limit ? t('hr.limit_hint', { amount: money(cat.limit) }) : undefined}>
          <select className="input" value={f.category} onChange={(e) => set('category', e.target.value)}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={over ? t('hr.note_over') : t('hr.note')} hint={over ? t('hr.note_over_hint') : t('hr.optional')}>
          <textarea className="input" rows={2} maxLength={1000} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** A receipt's total in its own currency, e.g. €18.50; the bare number when the code is not one Intl knows. */
function inCurrency(total: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(total);
  } catch {
    return `${total} ${currency}`.trim();
  }
}

interface ReceiptReading {
  ready: boolean;
  readable: boolean;
  merchant: string | null;
  day: string | null;
  amount: number | null;
  foreign: { total: number; currency: string } | null;
  description: string | null;
  category: string | null;
}

interface ExpenseDetail extends ExpenseRow {
  files: { id: string; name: string; mime: string; size: number; created_at: string }[];
  can_edit: boolean;
  can_decide: boolean;
  can_pay: boolean;
  submitted_at: string | null;
  decided_at: string | null;
}

function ExpenseDialog({ id, onClose, onEdit, onChange }: { id: string; onClose: () => void; onEdit: (e: ExpenseRow) => void; onChange: () => Promise<void> }) {
  const { data: e, reload } = useAction<ExpenseDetail>('hr.expense', { id });
  const { busy, run } = useSave();
  const input = useRef<HTMLInputElement>(null);
  const [refuse, setRefuse] = useState(false);
  const [pay, setPay] = useState(false);
  const done = async () => {
    await reload();
    await onChange();
  };
  const add = (f: File | undefined) => {
    if (input.current) input.current.value = '';
    if (!f || !e) return;
    void run(async () => {
      if (f.size > MAX_FILE) throw new Error(t('hr.file_too_big'));
      await upload('hr.add_receipt', { id: e.id, name: f.name, data: await readBase64(f) });
      await done();
    }, t('hr.receipt_added'));
  };
  return (
    <Dialog
      title={e ? `${e.name} · ${e.description}` : t('hr.expense')}
      onClose={onClose}
      size="md"
      footer={
        e ? (
          <>
            {e.can_edit ? (
              <Button variant="ghost" icon="edit" onClick={() => onEdit(e)}>
                {t('hr.edit')}
              </Button>
            ) : null}
            <span className="foot-actions">
              {e.can_edit ? (
                <Button
                  variant="primary"
                  icon="send"
                  busy={busy}
                  disabled={!e.files.length}
                  onClick={() =>
                    void run(
                      async () => {
                        await action('hr.submit_expenses', { ids: [e.id] });
                        await done();
                      },
                      tn('hr.sent_n', 1),
                    )
                  }
                >
                  {t('hr.send')}
                </Button>
              ) : null}
              {e.can_decide ? (
                <>
                  <Button variant="ghost" icon="x" onClick={() => setRefuse(true)}>
                    {t('hr.refuse')}
                  </Button>
                  <Button
                    variant="primary"
                    icon="check"
                    busy={busy}
                    onClick={() =>
                      void run(async () => {
                        await action('hr.decide_expense', { id: e.id, approve: true });
                        await done();
                      }, t('hr.expense_approved'))
                    }
                  >
                    {t('hr.approve')}
                  </Button>
                </>
              ) : null}
              {e.can_pay ? (
                <Button variant="primary" icon="cash" onClick={() => setPay(true)}>
                  {t('hr.pay_back')}
                </Button>
              ) : null}
            </span>
          </>
        ) : null
      }
    >
      {!e ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted small">{t('hr.person')}</span>
              <strong>{e.user_name}</strong>
            </div>
            <div>
              <span className="muted small">{t('hr.day')}</span>
              <strong>{dayText(e.day)}</strong>
            </div>
            <div>
              <span className="muted small">{t('hr.category')}</span>
              <strong>{e.category_name}</strong>
            </div>
            <div>
              <span className="muted small">{t('hr.amount')}</span>
              <strong>{money(e.amount)}</strong>
            </div>
            <StatusBadge tone={EX_STATE[e.state].tone} icon={EX_STATE[e.state].icon} label={t(`hr.es_${e.state}` as MessageKey)} />
          </div>
          {e.over_limit ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} /> {t('hr.over_limit_note')}
            </p>
          ) : null}
          {e.note ? (
            <p>
              <span className="muted small">{t('hr.note')}</span>
              <br />
              {e.note}
            </p>
          ) : null}
          {e.state === 'refused' ? (
            <p className="callout callout-danger">
              <Icon name="x" size={16} /> {t('hr.refused_by', { name: e.decided_by_name ?? '', note: e.decision_note ?? '' })}
              {e.can_edit ? ` ${t('hr.refused_fix')}` : ''}
            </p>
          ) : e.decided_by_name ? (
            <p className="muted small">{t('hr.approved_by', { name: e.decided_by_name })}</p>
          ) : null}
          {e.state === 'paid' ? (
            <p className="muted small">
              {t('hr.paid_line', { how: t(`hr.pay_${e.paid_method ?? 'cash'}` as MessageKey), at: e.paid_at ? dateTime(e.paid_at) : '', name: e.paid_by_name ?? '' })}
              {e.paid_reference ? ` · ${e.paid_reference}` : ''}
            </p>
          ) : null}
          <section>
            <div className="prot-history-head">
              <h3>{t('hr.receipts')}</h3>
              {e.can_edit ? (
                <>
                  <input ref={input} type="file" accept={ACCEPT} capture="environment" hidden onChange={(ev) => add(ev.target.files?.[0])} />
                  <Button size="sm" variant="ghost" icon="plus" busy={busy} onClick={() => input.current?.click()}>
                    {t('hr.add_receipt')}
                  </Button>
                </>
              ) : null}
            </div>
            {e.files.length ? (
              <div className="list-card">
                {e.files.map((f) => (
                  <div key={f.id} className="list-row compact-move">
                    <button className="hr-file" onClick={() => void run(async () => openFile(await action('hr.receipt', { id: f.id })))}>
                      <span className="list-icon" aria-hidden="true">
                        <Icon name={f.mime === 'application/pdf' ? 'invoice' : 'image'} size={18} />
                      </span>
                      <span className="list-main">
                        <span className="list-title">{f.name}</span>
                        <span className="list-sub">{[size(f.size), dateTime(f.created_at)].join(' · ')}</span>
                      </span>
                      <Icon name="download" size={18} />
                    </button>
                    {e.can_edit ? (
                      <IconButton
                        icon="trash"
                        variant="ghost"
                        label={t('hr.remove_receipt')}
                        onClick={() =>
                          void run(async () => {
                            await action('hr.remove_receipt', { id: f.id });
                            await done();
                          })
                        }
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="callout callout-warn">
                <Icon name="alert" size={16} /> {t('hr.receipt_needed')}
              </p>
            )}
          </section>
        </div>
      )}
      {refuse && e ? (
        <ReasonDialog
          title={t('hr.refuse_expense', { name: e.name })}
          need
          label={t('hr.refuse_reason')}
          confirm={t('hr.refuse')}
          done={t('hr.expense_refused')}
          onClose={() => setRefuse(false)}
          onConfirm={async (note) => {
            await action('hr.decide_expense', { id: e.id, approve: false, note });
            setRefuse(false);
            await done();
          }}
        />
      ) : null}
      {pay && e ? (
        <PayDialog
          count={1}
          total={e.amount}
          onClose={() => setPay(false)}
          onPay={async (method, reference) => {
            await action('hr.pay_expenses', { ids: [e.id], method, reference });
            setPay(false);
            await done();
          }}
        />
      ) : null}
    </Dialog>
  );
}

export function PayDialog({
  count,
  total,
  title,
  body,
  toast,
  cta,
  onClose,
  onPay,
}: {
  count: number;
  total: number;
  title?: string;
  body?: string;
  toast?: string;
  cta?: string;
  onClose: () => void;
  onPay: (method: 'cash' | 'bank', reference: string | null) => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [method, setMethod] = useState<'cash' | 'bank'>('cash');
  const [reference, setReference] = useState('');
  const [tried, setTried] = useState(false);
  return (
    <Dialog
      title={title ?? t('hr.pay_title', { amount: money(total) })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="cash"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (method === 'bank' && !reference.trim()) return;
              void run(() => onPay(method, reference.trim() || null), toast ?? tn('hr.paid_toast', count, { amount: money(total) }));
            }}
          >
            {cta ?? t('hr.mark_paid')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">{body ?? tn('hr.pay_body', count)}</p>
        <div className="tk-billing hr-two">
          {(['cash', 'bank'] as const).map((m) => (
            <button type="button" key={m} className={`option-card${method === m ? ' selected' : ''}`} aria-pressed={method === m} onClick={() => setMethod(m)}>
              <Icon name={m === 'cash' ? 'cash' : 'bank'} size={20} />
              <strong>{t(`hr.pay_${m}` as MessageKey)}</strong>
            </button>
          ))}
        </div>
        {method === 'bank' ? (
          <Field label={t('hr.paid_ref')} error={tried && !reference.trim() ? t('hr.e_reference') : null} required>
            <input className="input" value={reference} maxLength={80} onChange={(e) => setReference(e.target.value)} data-autofocus />
          </Field>
        ) : null}
        <p className="small muted">{t('hr.pay_books_hint')}</p>
      </div>
    </Dialog>
  );
}

// ---- recruitment ------------------------------------------------------------------------

interface JobRow {
  id: string;
  title: string;
  location: string | null;
  location_name: string | null;
  department: string | null;
  kind: (typeof KINDS)[number];
  description: string | null;
  openings: number;
  open: boolean;
  public: boolean;
  closes: string | null;
  manager: string | null;
  active: number;
  new: number;
  hired: number;
  total: number;
}
interface Jobs {
  jobs: JobRow[];
  careers: boolean;
  careers_intro: string | null;
  locations: Person[];
  people: Person[];
}
interface ApplicantRow {
  id: string;
  job: string;
  job_title: string;
  name: string;
  email: string | null;
  phone: string | null;
  stage: Stage;
  rating: number;
  source: (typeof SOURCES)[number];
  message: string | null;
  interview_at: string | null;
  interviewer_name: string | null;
  refuse_reason: string | null;
  stage_at: string;
  created_at: string;
  files: number;
  referred_by_name: string | null;
  referral_note: string | null;
}

function RecruitTab({ onChange }: { onChange: () => Promise<void> }) {
  const [params, setParams] = useSearchParams();
  const job = params.get('job');
  const [open, setOpen] = useState(true);
  const { data, reload } = useAction<Jobs>('hr.jobs', { open });
  const [form, setForm] = useState<JobRow | 'new' | null>(null);
  const [careers, setCareers] = useState(false);
  const pick = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('job', id);
    else p.delete('job');
    p.delete('a');
    setParams(p, { replace: true });
  };
  if (job) return <Pipeline job={job} onBack={() => pick(null)} onChange={onChange} />;
  return (
    <>
      <section className="panel">
        <div className="panel-head">
          <div className="segmented" role="tablist" aria-label={t('hr.jobs_show')}>
            {[true, false].map((o) => (
              <button key={String(o)} role="tab" aria-selected={open === o} className={open === o ? 'active' : ''} onClick={() => setOpen(o)}>
                {o ? t('hr.jobs_open') : t('hr.jobs_closed')}
              </button>
            ))}
          </div>
          <div className="hr-ex-tools">
            <Button size="sm" variant="ghost" icon="globe" onClick={() => setCareers(true)}>
              {data?.careers ? t('hr.careers_on') : t('hr.careers_off')}
            </Button>
            <Button size="sm" variant="primary" icon="plus" onClick={() => setForm('new')}>
              {t('hr.new_job')}
            </Button>
          </div>
        </div>
        {!data ? (
          <Skeleton h={200} />
        ) : data.jobs.length ? (
          <ul className="tk-cards">
            {data.jobs.map((j) => (
              <li key={j.id}>
                <button className="tk-card hr-job" onClick={() => pick(j.id)}>
                  <div className="tk-card-head">
                    <span className="tk-code">{t(`hr.kind_${j.kind}` as MessageKey)}</span>
                    {j.new ? <StatusBadge tone="primary" icon="sparkle" label={tn('hr.n_new', j.new)} /> : null}
                  </div>
                  <strong className="tk-card-title">{j.title}</strong>
                  <span className="muted small">{[j.department, j.location_name].filter(Boolean).join(' · ') || t('hr.no_place')}</span>
                  <div className="tk-card-meter">
                    <span className="small">{t('hr.hired_of', { hired: j.hired, openings: j.openings })}</span>
                    <Meter value={j.hired} max={j.openings} label={t('hr.hired_of', { hired: j.hired, openings: j.openings })} />
                  </div>
                  <div className="tk-card-foot small">
                    <span className="muted">
                      <Icon name="users" size={14} /> {tn('hr.n_in_running', j.active)}
                    </span>
                    {j.public && data.careers && j.open ? (
                      <span className="muted">
                        <Icon name="globe" size={14} /> {t('hr.on_careers')}
                      </span>
                    ) : null}
                    {j.closes ? (
                      <span className="muted">
                        <Icon name="calendar" size={14} /> {dayText(j.closes)}
                      </span>
                    ) : null}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon="users"
            title={open ? t('hr.no_jobs') : t('hr.no_closed_jobs')}
            body={open ? t('hr.no_jobs_hint') : undefined}
            action={
              open ? (
                <Button variant="primary" icon="plus" onClick={() => setForm('new')}>
                  {t('hr.new_job')}
                </Button>
              ) : undefined
            }
          />
        )}
      </section>
      {form && data ? (
        <JobForm
          initial={form === 'new' ? null : form}
          setup={data}
          onClose={() => setForm(null)}
          onSaved={async (id) => {
            setForm(null);
            await reload();
            await onChange();
            if (form === 'new') pick(id);
          }}
        />
      ) : null}
      {careers && data ? (
        <CareersDialog
          initial={data}
          onClose={() => setCareers(false)}
          onSaved={async () => {
            setCareers(false);
            await reload();
          }}
        />
      ) : null}
    </>
  );
}

export const careersUrl = (tenant: string) => `${window.location.origin}/careers/${tenant}`;

function CareersDialog({ initial, onClose, onSaved }: { initial: Jobs; onClose: () => void; onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const { toast } = useUi();
  const { busy, run } = useSave();
  const [on, setOn] = useState(initial.careers);
  const [intro, setIntro] = useState(initial.careers_intro ?? '');
  const url = careersUrl(tenant?.slug ?? '');
  return (
    <Dialog
      title={t('hr.careers_title')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() =>
              void run(async () => {
                await action('hr.save_careers', { careers: on, careers_intro: intro.trim() || null });
                await onSaved();
              }, t('hr.saved'))
            }
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <div className="setting-row">
          <div>
            <strong>{t('hr.careers_switch')}</strong>
            <p className="muted">{t('hr.careers_switch_hint')}</p>
          </div>
          <Switch checked={on} onChange={setOn} label={t('hr.careers_switch')} />
        </div>
        <Field label={t('hr.careers_intro')} hint={t('hr.careers_intro_hint')}>
          <textarea className="input" rows={3} maxLength={2000} value={intro} onChange={(e) => setIntro(e.target.value)} />
        </Field>
        <div className="hr-link">
          <code>{url}</code>
          <Button
            size="sm"
            icon="copy"
            onClick={() =>
              void navigator.clipboard
                .writeText(url)
                .then(() => toast(t('hr.copied')))
                .catch(() => {})
            }
          >
            {t('hr.copy')}
          </Button>
          {initial.careers ? (
            <a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noreferrer">
              <Icon name="eye" size={16} /> {t('hr.open_page')}
            </a>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

function JobForm({ initial, setup, onClose, onSaved }: { initial: JobRow | null; setup: Jobs; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: dirChoices } = useAction<{ departments: { id: string; name: string }[] }>('hr.directory_choices');
  const [f, setF] = useState({
    title: initial?.title ?? '',
    department: initial?.department ?? '',
    location: initial?.location ?? '',
    kind: initial?.kind ?? ('full_time' as JobRow['kind']),
    openings: String(initial?.openings ?? 1),
    description: initial?.description ?? '',
    public: initial?.public ?? true,
    closes: initial?.closes ?? '',
    manager: initial?.manager ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const openings = Number(f.openings);
  const ok = f.title.trim().length >= 2 && Number.isInteger(openings) && openings >= 1;
  return (
    <Dialog
      title={initial ? t('hr.edit_job') : t('hr.new_job')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!ok) return;
              void run(async () => {
                const j = await action<{ id: string }>('hr.save_job', {
                  id: initial?.id ?? null,
                  title: f.title.trim(),
                  department: f.department.trim() || null,
                  location: f.location || null,
                  kind: f.kind,
                  openings,
                  description: f.description.trim() || null,
                  public: f.public,
                  closes: f.closes || null,
                  manager: f.manager || null,
                });
                await onSaved(j.id);
              }, t('hr.saved'));
            }}
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('hr.job_title')} error={tried && f.title.trim().length < 2 ? t('hr.e_job_title') : null} required>
          <input className="input" value={f.title} maxLength={100} placeholder={t('hr.job_title_ph')} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <div className="form-grid">
          <Field label={t('hr.department')} hint={t('hr.optional')}>
            <input className="input" value={f.department} maxLength={80} list="hr-job-departments" onChange={(e) => set('department', e.target.value)} />
          </Field>
          <datalist id="hr-job-departments">
            {(dirChoices?.departments ?? []).map((d) => (
              <option key={d.id} value={d.name} />
            ))}
          </datalist>
          <Field label={t('hr.place')} hint={t('hr.optional')}>
            <select className="input" value={f.location} onChange={(e) => set('location', e.target.value)}>
              <option value="">{t('hr.any_place')}</option>
              {setup.locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="tk-billing">
          {KINDS.map((k) => (
            <button type="button" key={k} className={`option-card${f.kind === k ? ' selected' : ''}`} aria-pressed={f.kind === k} onClick={() => set('kind', k)}>
              <strong>{t(`hr.kind_${k}` as MessageKey)}</strong>
            </button>
          ))}
        </div>
        <div className="form-grid">
          <Field label={t('hr.openings')} error={tried && !(Number.isInteger(openings) && openings >= 1) ? t('hr.e_openings') : null}>
            <input className="input" type="number" min={1} max={500} value={f.openings} onChange={(e) => set('openings', e.target.value)} />
          </Field>
          <Field label={t('hr.closes')} hint={t('hr.optional')}>
            <input className="input" type="date" value={f.closes} min={todayIso()} onChange={(e) => set('closes', e.target.value)} />
          </Field>
        </div>
        <Field label={t('hr.hiring_manager')} hint={t('hr.optional')}>
          <select className="input" value={f.manager} onChange={(e) => set('manager', e.target.value)}>
            <option value="">—</option>
            {setup.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('hr.job_description')} hint={t('hr.job_description_hint')}>
          <textarea className="input" rows={6} maxLength={5000} value={f.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('hr.show_on_careers')}</strong>
            <p className="muted">{setup.careers ? t('hr.show_on_careers_hint') : t('hr.careers_page_off')}</p>
          </div>
          <Switch checked={f.public} onChange={(v) => set('public', v)} label={t('hr.show_on_careers')} />
        </div>
      </div>
    </Dialog>
  );
}

function Stars({ value, onChange, label }: { value: number; onChange?: (n: number) => void; label: string }) {
  return (
    <span className="hr-stars" role={onChange ? 'radiogroup' : 'img'} aria-label={`${label}: ${value}/3`}>
      {[1, 2, 3].map((n) =>
        onChange ? (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={tn('hr.n_stars', n)} className={n <= value ? 'on' : ''} onClick={() => onChange(value === n ? 0 : n)}>
            <Icon name="star" size={18} />
          </button>
        ) : (
          <span key={n} className={n <= value ? 'on' : ''} aria-hidden="true">
            <Icon name="star" size={14} />
          </span>
        ),
      )}
    </span>
  );
}

function Pipeline({ job, onBack, onChange }: { job: string; onBack: () => void; onChange: () => Promise<void> }) {
  const [params, setParams] = useSearchParams();
  const { toast } = useUi();
  const [search, setSearch] = useState('');
  const { data, reload } = useAction<{ applicants: ApplicantRow[]; job: JobRow }>('hr.applicants', { job, search: search.trim() || null });
  const { data: setup } = useAction<Jobs>('hr.jobs', { open: true });
  const { busy, run } = useSave();
  const [add, setAdd] = useState(false);
  const [edit, setEdit] = useState(false);
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<Stage | null>(null);
  const [refuse, setRefuse] = useState<ApplicantRow | null>(null);
  const opened = params.get('a');
  const openApplicant = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('a', id);
    else p.delete('a');
    setParams(p, { replace: true });
  };
  const done = async () => {
    await reload();
    await onChange();
  };
  const move = (a: ApplicantRow, stage: Stage) => {
    if (a.stage === stage) return;
    if (stage === 'refused') return setRefuse(a);
    void run(async () => {
      const r = await action<{ job_closed?: boolean }>('hr.move_applicant', { id: a.id, stage });
      await done();
      toast(r.job_closed ? t('hr.job_now_full', { name: a.name }) : t('hr.moved_toast', { name: a.name, stage: t(`hr.stage_${stage}` as MessageKey) }));
    });
  };
  const job_ = data?.job as (JobRow & { open: boolean }) | undefined;
  return (
    <>
      <nav className="tk-crumbs" aria-label={t('hr.crumbs')}>
        <button className="link" onClick={onBack}>
          {t('hr.tab_recruit')}
        </button>
        <Icon name="chevronRight" size={14} />
        <span>{job_?.title ?? '…'}</span>
      </nav>
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>{job_?.title ?? '…'}</h2>
            {job_ ? (
              <p className="muted small">
                {[t(`hr.kind_${job_.kind}` as MessageKey), job_.department, t('hr.openings_n', { n: job_.openings })].filter(Boolean).join(' · ')}
                {!job_.open ? ` · ${t('hr.closed')}` : ''}
              </p>
            ) : null}
          </div>
          <div className="hr-ex-tools">
            <label className="search">
              <Icon name="search" size={16} />
              <input className="input" type="search" placeholder={t('hr.search_applicants')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </label>
            {job_ ? (
              <>
                <Button size="sm" variant="ghost" icon="edit" onClick={() => setEdit(true)}>
                  {t('hr.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={job_.open ? 'lock' : 'undo'}
                  busy={busy}
                  onClick={() =>
                    void run(
                      async () => {
                        await action('hr.set_job_open', { id: job, open: !job_.open });
                        await done();
                      },
                      job_.open ? t('hr.job_closed_toast') : t('hr.job_opened_toast'),
                    )
                  }
                >
                  {job_.open ? t('hr.close_job') : t('hr.reopen_job')}
                </Button>
              </>
            ) : null}
            <Button size="sm" variant="primary" icon="plus" disabled={!job_?.open} onClick={() => setAdd(true)}>
              {t('hr.add_applicant')}
            </Button>
          </div>
        </div>
        <p className="muted small tk-board-tools">{t('hr.drag_hint')}</p>
        {!data ? (
          <Skeleton h={300} />
        ) : (
          <div className="tk-board hr-board" style={{ ['--cols' as string]: STAGES.length }}>
            {STAGES.map((s) => {
              const list = data.applicants.filter((a) => a.stage === s);
              return (
                <section
                  key={s}
                  className={`tk-col${over === s ? ' over' : ''}${s === 'hired' ? ' done' : ''}`}
                  aria-label={t(`hr.stage_${s}` as MessageKey)}
                  onDragOver={(e) => {
                    if (!drag) return;
                    e.preventDefault();
                    setOver(s);
                  }}
                  onDragLeave={() => setOver((o) => (o === s ? null : o))}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOver(null);
                    const a = data.applicants.find((x) => x.id === drag);
                    setDrag(null);
                    if (a) move(a, s);
                  }}
                >
                  <div className="tk-col-head">
                    <strong>{t(`hr.stage_${s}` as MessageKey)}</strong>
                    <span className="muted small">{list.length}</span>
                  </div>
                  <div className="tk-col-body">
                    {list.length ? (
                      list.map((a) => (
                        <article key={a.id} className="tk-task" draggable onDragStart={() => setDrag(a.id)} onDragEnd={() => setDrag(null)}>
                          <button className="tk-task-main" onClick={() => openApplicant(a.id)}>
                            <span className="hr-app-head">
                              <strong>{a.name}</strong>
                              {a.rating ? <Stars value={a.rating} label={t('hr.rating')} /> : null}
                            </span>
                            <span className="tk-task-meta small muted">
                              <span>{a.referred_by_name ? t('ref.by', { name: a.referred_by_name }) : t(`hr.src_${a.source}` as MessageKey)}</span>
                              {a.files ? (
                                <span>
                                  <Icon name="invoice" size={14} /> {t('hr.cv')}
                                </span>
                              ) : null}
                              {a.interview_at && s !== 'hired' && s !== 'refused' ? (
                                <span>
                                  <Icon name="calendar" size={14} /> {dateTime(a.interview_at)}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </article>
                      ))
                    ) : (
                      <p className="muted small tk-col-empty">{t('hr.nobody_here')}</p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </section>
      {add && job_ ? (
        <ApplicantForm
          job={job_}
          onClose={() => setAdd(false)}
          onSaved={async (id) => {
            setAdd(false);
            await done();
            openApplicant(id);
          }}
        />
      ) : null}
      {edit && job_ && setup ? (
        <JobForm
          initial={job_}
          setup={setup}
          onClose={() => setEdit(false)}
          onSaved={async () => {
            setEdit(false);
            await done();
          }}
        />
      ) : null}
      {opened ? <ApplicantDialog key={opened} id={opened} onClose={() => openApplicant(null)} onMove={move} onChange={done} /> : null}
      {refuse ? (
        <ReasonDialog
          title={t('hr.refuse_applicant', { name: refuse.name })}
          need
          label={t('hr.refuse_applicant_reason')}
          confirm={t('hr.not_taken')}
          done={t('hr.applicant_refused')}
          onClose={() => setRefuse(null)}
          onConfirm={async (reason) => {
            await action('hr.move_applicant', { id: refuse.id, stage: 'refused', refuse_reason: reason });
            setRefuse(null);
            await done();
          }}
        />
      ) : null}
    </>
  );
}

function ApplicantForm({ job, onClose, onSaved }: { job: JobRow; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ name: '', email: '', phone: '', source: 'walk_in' as (typeof SOURCES)[number], message: '' });
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const emailOk = !f.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog
      title={t('hr.add_applicant_to', { job: job.title })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (f.name.trim().length < 2 || !emailOk) return;
              void run(async () => {
                const a = await action<{ id: string }>('hr.save_applicant', {
                  job: job.id,
                  name: f.name.trim(),
                  email: f.email.trim() || null,
                  phone: f.phone.trim() || null,
                  source: f.source,
                  message: f.message.trim() || null,
                });
                if (file) await upload('hr.add_cv', { id: a.id, name: file.name, data: await readBase64(file) });
                await onSaved(a.id);
              }, t('hr.applicant_added'));
            }}
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('hr.name')} error={tried && f.name.trim().length < 2 ? t('hr.e_name') : null} required>
          <input className="input" value={f.name} maxLength={120} onChange={(e) => set('name', e.target.value)} data-autofocus />
        </Field>
        <div className="form-grid">
          <Field label={t('hr.email')} error={tried && !emailOk ? t('hr.e_email') : null}>
            <input className="input" type="email" value={f.email} maxLength={160} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label={t('hr.phone')}>
            <input className="input" type="tel" value={f.phone} maxLength={40} onChange={(e) => set('phone', e.target.value)} />
          </Field>
        </div>
        <Field label={t('hr.source')}>
          <select className="input" value={f.source} onChange={(e) => set('source', e.target.value as (typeof SOURCES)[number])}>
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`hr.src_${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('hr.about')} hint={t('hr.optional')}>
          <textarea className="input" rows={3} maxLength={5000} value={f.message} onChange={(e) => set('message', e.target.value)} />
        </Field>
        <div className="hr-receipt-pick">
          <input ref={input} type="file" accept={ACCEPT} hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <Button icon={file ? 'checkCircle' : 'invoice'} variant={file ? 'secondary' : 'ghost'} onClick={() => input.current?.click()}>
            {file ? file.name : t('hr.add_cv')}
          </Button>
          <span className="small muted">{t('hr.cv_hint')}</span>
        </div>
      </div>
    </Dialog>
  );
}

interface ApplicantDetail extends ApplicantRow {
  notes: { id: string; text: string; at: string; by: string }[];
  cv: { id: string; name: string; mime: string; size: number; created_at: string }[];
  other_applications: { id: string; stage: Stage; title: string }[];
  people: Person[];
  interviewer: string | null;
  hired_at: string | null;
}

function ApplicantDialog({ id, onClose, onMove, onChange }: { id: string; onClose: () => void; onMove: (a: ApplicantRow, s: Stage) => void; onChange: () => Promise<void> }) {
  const { me } = useSession();
  const { data: a, reload } = useAction<ApplicantDetail>('hr.applicant', { id });
  const { busy, run } = useSave();
  const [note, setNote] = useState('');
  const [plan, setPlan] = useState<{ at: string; who: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const done = async () => {
    await reload();
    await onChange();
  };
  const closed = a?.stage === 'hired' || a?.stage === 'refused';
  const toLocal = (iso: string) => {
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  };
  return (
    <Dialog title={a ? a.name : t('hr.applicant')} onClose={onClose} size="lg">
      {!a ? (
        <Skeleton h={320} />
      ) : (
        <div className="form-stack mkt-dlg">
          <div className="item-summary">
            <div>
              <span className="muted small">{t('hr.position')}</span>
              <strong>{a.job_title}</strong>
            </div>
            <div>
              <span className="muted small">{t('hr.applied')}</span>
              <strong>{dayText(a.created_at.slice(0, 10))}</strong>
            </div>
            <div>
              <span className="muted small">{t('hr.source')}</span>
              <strong>{a.referred_by_name ? t('ref.by', { name: a.referred_by_name }) : t(`hr.src_${a.source}` as MessageKey)}</strong>
            </div>
            <div>
              <span className="muted small">{t('hr.rating')}</span>
              <Stars
                value={a.rating}
                label={t('hr.rating')}
                onChange={(n) =>
                  void run(async () => {
                    await action('hr.rate_applicant', { id: a.id, rating: n });
                    await done();
                  })
                }
              />
            </div>
          </div>
          <div className="tk-stage-pick" role="group" aria-label={t('hr.stage')}>
            {STAGES.map((s) => (
              <button key={s} type="button" className={`tk-stage${a.stage === s ? ' active' : ''}${s === 'refused' ? ' hr-refuse' : ''}`} aria-pressed={a.stage === s} onClick={() => onMove(a, s)}>
                {a.stage === s ? <Icon name="check" size={14} /> : null}
                {t(`hr.stage_${s}` as MessageKey)}
              </button>
            ))}
          </div>
          {a.referral_note ? (
            <p className="callout">
              <Icon name="gift" size={16} /> {t('ref.note_from', { name: a.referred_by_name ?? '' })} {a.referral_note}
            </p>
          ) : null}
          {a.stage === 'refused' && a.refuse_reason ? (
            <p className="callout callout-danger">
              <Icon name="x" size={16} /> {a.refuse_reason}
            </p>
          ) : null}
          {a.other_applications.length ? (
            <p className="callout callout-info">
              <Icon name="users" size={16} /> {t('hr.also_applied', { list: a.other_applications.map((o) => `${o.title} (${t(`hr.stage_${o.stage}` as MessageKey)})`).join(', ') })}
            </p>
          ) : null}
          <div className="shop-detail">
            <div className="form-stack">
              <section>
                <h3>{t('hr.contact')}</h3>
                <ul className="hr-contact">
                  {a.email ? (
                    <li>
                      <Icon name="mail" size={16} /> <a href={`mailto:${a.email}`}>{a.email}</a>
                    </li>
                  ) : null}
                  {a.phone ? (
                    <li>
                      <Icon name="phone" size={16} /> <a href={`tel:${a.phone.replace(/\s/g, '')}`}>{a.phone}</a>
                    </li>
                  ) : null}
                  {!a.email && !a.phone ? <li className="muted">{t('hr.no_contact')}</li> : null}
                </ul>
              </section>
              {a.message ? (
                <section>
                  <h3>{t('hr.about')}</h3>
                  <p className="fs-note">{a.message}</p>
                </section>
              ) : null}
              <section>
                <div className="prot-history-head">
                  <h3>{t('hr.interview')}</h3>
                  {!closed && !plan ? (
                    <Button size="sm" variant="ghost" icon="calendar" onClick={() => setPlan({ at: a.interview_at ? toLocal(a.interview_at) : '', who: a.interviewer ?? me?.userId ?? '' })}>
                      {a.interview_at ? t('hr.change') : t('hr.plan_interview')}
                    </Button>
                  ) : null}
                </div>
                {plan ? (
                  <div className="form-stack">
                    <div className="form-grid">
                      <Field label={t('hr.when')}>
                        <input className="input" type="datetime-local" value={plan.at} onChange={(e) => setPlan({ ...plan, at: e.target.value })} />
                      </Field>
                      <Field label={t('hr.interviewer')}>
                        <select className="input" value={plan.who} onChange={(e) => setPlan({ ...plan, who: e.target.value })}>
                          {a.people.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="hr-inline-actions">
                      {a.interview_at ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          busy={busy}
                          onClick={() =>
                            void run(async () => {
                              await action('hr.schedule_interview', { id: a.id, at: null });
                              setPlan(null);
                              await done();
                            }, t('hr.interview_removed'))
                          }
                        >
                          {t('hr.remove_interview')}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" onClick={() => setPlan(null)}>
                        {t('hr.back')}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        busy={busy}
                        disabled={!plan.at}
                        onClick={() =>
                          void run(async () => {
                            await action('hr.schedule_interview', { id: a.id, at: new Date(plan.at).toISOString(), interviewer: plan.who || null });
                            setPlan(null);
                            await done();
                          }, t('hr.interview_planned'))
                        }
                      >
                        {t('hr.save')}
                      </Button>
                    </div>
                  </div>
                ) : a.interview_at ? (
                  <p>
                    <strong>{dateTime(a.interview_at)}</strong>
                    {a.interviewer_name ? <span className="muted"> · {t('hr.with', { name: a.interviewer_name })}</span> : null}
                  </p>
                ) : (
                  <p className="muted">{t('hr.no_interview')}</p>
                )}
              </section>
              <section>
                <div className="prot-history-head">
                  <h3>{t('hr.cv_files')}</h3>
                  <input
                    ref={input}
                    type="file"
                    accept={ACCEPT}
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (input.current) input.current.value = '';
                      if (!f) return;
                      void run(async () => {
                        if (f.size > MAX_FILE) throw new Error(t('hr.file_too_big'));
                        await upload('hr.add_cv', { id: a.id, name: f.name, data: await readBase64(f) });
                        await done();
                      }, t('hr.file_added'));
                    }}
                  />
                  <Button size="sm" variant="ghost" icon="plus" busy={busy} onClick={() => input.current?.click()}>
                    {t('hr.add_file')}
                  </Button>
                </div>
                {a.cv.length ? (
                  <div className="list-card">
                    {a.cv.map((f) => (
                      <button key={f.id} className="list-row compact-move" onClick={() => void run(async () => openFile(await action('hr.cv', { id: f.id })))}>
                        <span className="list-icon" aria-hidden="true">
                          <Icon name={f.mime === 'application/pdf' ? 'invoice' : 'image'} size={18} />
                        </span>
                        <span className="list-main">
                          <span className="list-title">{f.name}</span>
                          <span className="list-sub">{size(f.size)}</span>
                        </span>
                        <Icon name="download" size={18} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="muted">{t('hr.no_cv')}</p>
                )}
              </section>
            </div>
            <section className="form-stack">
              <h3>{t('hr.notes')}</h3>
              <div className="hr-note-box">
                <textarea className="input" rows={2} maxLength={2000} placeholder={t('hr.write_note')} value={note} onChange={(e) => setNote(e.target.value)} aria-label={t('hr.write_note')} />
                <Button
                  size="sm"
                  icon="send"
                  busy={busy}
                  disabled={!note.trim()}
                  onClick={() =>
                    void run(async () => {
                      await action('hr.note_applicant', { id: a.id, text: note.trim() });
                      setNote('');
                      await reload();
                    })
                  }
                >
                  {t('hr.add_note')}
                </Button>
              </div>
              {a.notes.length ? (
                <ul className="hr-notes">
                  {a.notes.map((n) => (
                    <li key={n.id}>
                      <span className="muted small">{[n.by, dateTime(n.at)].filter(Boolean).join(' · ')}</span>
                      <p className="fs-note">{n.text}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('hr.no_notes')}</p>
              )}
            </section>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---- settings ---------------------------------------------------------------------------

interface Setup {
  year: number;
  types: LeaveType[];
  holidays: { id: string; day: string; name: string }[];
  work_days: number[];
  people: (Person & { days: { type: string; given: number | null; taken: number; waiting: number; left: number | null }[] })[];
}

function SettingsTab() {
  const [year, setYear] = useState(Number(todayIso().slice(0, 4)));
  const { data, reload } = useAction<Setup>('hr.leave_setup', { year });
  const { data: cats, reload: reloadCats } = useAction<Category[]>('hr.categories');
  const { busy, run } = useSave();
  const [type, setType] = useState<LeaveType | 'new' | null>(null);
  const [cell, setCell] = useState<{ person: Person; type: LeaveType; given: number | null } | null>(null);
  const [holiday, setHoliday] = useState(false);
  const [cat, setCat] = useState<Category | 'new' | null>(null);
  const [all, setAll] = useState<LeaveType | null>(null);
  if (!data) return <Skeleton h={400} />;
  const limited = data.types.filter((k) => k.limited && k.active);
  return (
    <>
      <section className="panel">
        <div className="panel-head">
          <h2>{t('hr.kinds_title')}</h2>
          <div className="hr-ex-tools">
            {!data.types.length ? (
              <Button
                size="sm"
                busy={busy}
                onClick={() =>
                  void run(async () => {
                    await action('hr.add_default_types');
                    await reload();
                  }, t('hr.kinds_added'))
                }
              >
                {t('hr.add_usual_kinds')}
              </Button>
            ) : null}
            <Button size="sm" variant="primary" icon="plus" onClick={() => setType('new')}>
              {t('hr.new_kind')}
            </Button>
          </div>
        </div>
        {data.types.length ? (
          <ul className="hr-kind-list">
            {data.types.map((k) => (
              <li key={k.id} className={k.active ? '' : 'muted'}>
                <span className={`hr-dot ${tone(k.color)}`} aria-hidden="true" />
                <div className="hr-leave-main">
                  <strong>{k.name}</strong>
                  <span className="muted small">
                    {[
                      k.paid ? t('hr.paid') : t('hr.unpaid'),
                      k.limited ? tn('hr.n_days_year', k.default_days) : t('hr.no_limit'),
                      k.half_days ? t('hr.half_days_ok') : null,
                      k.active ? null : t('hr.not_used'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                <IconButton icon="edit" variant="ghost" label={t('hr.edit_kind', { name: k.name })} onClick={() => setType(k)} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t('hr.no_kinds_yet')}</p>
        )}
        <p className="small muted">{t('hr.kinds_law')}</p>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t('hr.days_given_title', { year })}</h2>
          <div className="hr-month">
            <IconButton icon="back" label={t('hr.prev_year')} variant="ghost" onClick={() => setYear(year - 1)} />
            <strong>{year}</strong>
            <IconButton icon="chevronRight" label={t('hr.next_year')} variant="ghost" onClick={() => setYear(year + 1)} />
          </div>
        </div>
        {limited.length ? (
          <>
            <div className="hr-ex-tools hr-give-all">
              {limited.map((k) => (
                <Button key={k.id} size="sm" variant="ghost" icon="users" onClick={() => setAll(k)}>
                  {t('hr.give_everyone', { kind: k.name })}
                </Button>
              ))}
            </div>
            <div className="hr-cal-wrap" tabIndex={0} aria-label={t('hr.days_given_title', { year })}>
              <table className="tk-sheet hr-given">
                <thead>
                  <tr>
                    <th scope="col">{t('hr.person')}</th>
                    {limited.map((k) => (
                      <th key={k.id} scope="col">
                        {k.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.people.map((p) => (
                    <tr key={p.id}>
                      <th scope="row">{p.name}</th>
                      {limited.map((k) => {
                        const d = p.days.find((x) => x.type === k.id)!;
                        return (
                          <td key={k.id}>
                            <button className="tk-cell" onClick={() => setCell({ person: p, type: k, given: d.given })} aria-label={t('hr.set_days_for', { name: p.name, kind: k.name })}>
                              {d.given === null ? (
                                <span className="muted">{t('hr.not_given')}</span>
                              ) : (
                                <>
                                  <strong>{d.left}</strong>
                                  <span className="muted small"> / {d.given}</span>
                                </>
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="small muted">{t('hr.given_hint')}</p>
          </>
        ) : (
          <p className="muted">{t('hr.no_limited_kinds')}</p>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t('hr.week_title')}</h2>
        </div>
        <div className="hr-weekdays" role="group" aria-label={t('hr.week_title')}>
          {WEEKDAYS.map((d) => {
            const on = data.work_days.includes(d);
            return (
              <button
                key={d}
                type="button"
                className={`chip${on ? ' active' : ''}`}
                aria-pressed={on}
                onClick={() =>
                  void run(async () => {
                    await action('hr.save_work_days', { work_days: on ? data.work_days.filter((x) => x !== d) : [...data.work_days, d] });
                    await reload();
                  })
                }
              >
                {weekdayName(d)}
              </button>
            );
          })}
        </div>
        <p className="small muted">{t('hr.week_hint')}</p>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t('hr.holidays_title', { year })}</h2>
          <div className="hr-ex-tools">
            <Button
              size="sm"
              variant="ghost"
              icon="copy"
              busy={busy}
              onClick={() =>
                void run(
                  async () => {
                    const r = await action<{ added: number }>('hr.copy_holidays', { year });
                    await reload();
                    if (!r.added) throw new Error(t('hr.nothing_to_copy', { year: year - 1 }));
                  },
                  t('hr.holidays_copied', { year: year - 1 }),
                )
              }
            >
              {t('hr.copy_from', { year: year - 1 })}
            </Button>
            <Button size="sm" variant="primary" icon="plus" onClick={() => setHoliday(true)}>
              {t('hr.add_holiday')}
            </Button>
          </div>
        </div>
        {data.holidays.length ? (
          <ul className="hr-holidays">
            {data.holidays.map((h) => (
              <li key={h.id}>
                <Icon name="star" size={16} />
                <strong>{dayText(h.day)}</strong>
                <span className="muted">{h.name}</span>
                <IconButton
                  icon="trash"
                  variant="ghost"
                  label={t('hr.remove_holiday', { name: h.name })}
                  onClick={() =>
                    void run(async () => {
                      await action('hr.remove_holiday', { id: h.id });
                      await reload();
                    }, t('hr.holiday_removed'))
                  }
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t('hr.no_holidays')}</p>
        )}
        <p className="small muted">{t('hr.holidays_hint')}</p>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t('hr.categories_title')}</h2>
          <div className="hr-ex-tools">
            {cats && !cats.length ? (
              <Button
                size="sm"
                busy={busy}
                onClick={() =>
                  void run(async () => {
                    await action('hr.add_default_categories');
                    await reloadCats();
                  }, t('hr.categories_added'))
                }
              >
                {t('hr.add_usual_categories')}
              </Button>
            ) : null}
            <Button size="sm" variant="primary" icon="plus" onClick={() => setCat('new')}>
              {t('hr.new_category')}
            </Button>
          </div>
        </div>
        {cats?.length ? (
          <ul className="hr-kind-list">
            {cats.map((c) => (
              <li key={c.id} className={c.active ? '' : 'muted'}>
                <Icon name="receipt" size={18} />
                <div className="hr-leave-main">
                  <strong>{c.name}</strong>
                  <span className="muted small">{[c.limit ? t('hr.limit_line', { amount: money(c.limit) }) : t('hr.no_limit'), c.active ? null : t('hr.not_used')].filter(Boolean).join(' · ')}</span>
                </div>
                <IconButton icon="edit" variant="ghost" label={t('hr.edit_category', { name: c.name })} onClick={() => setCat(c)} />
              </li>
            ))}
          </ul>
        ) : cats ? (
          <p className="muted">{t('hr.no_categories_yet')}</p>
        ) : (
          <Skeleton h={80} />
        )}
      </section>

      {type ? (
        <TypeForm
          initial={type === 'new' ? null : type}
          onClose={() => setType(null)}
          onSaved={async () => {
            setType(null);
            await reload();
          }}
        />
      ) : null}
      {cell ? (
        <DaysDialog
          {...cell}
          year={year}
          onClose={() => setCell(null)}
          onSaved={async () => {
            setCell(null);
            await reload();
          }}
        />
      ) : null}
      {all ? (
        <GiveAllDialog
          type={all}
          year={year}
          onClose={() => setAll(null)}
          onSaved={async () => {
            setAll(null);
            await reload();
          }}
        />
      ) : null}
      {holiday ? (
        <HolidayDialog
          year={year}
          onClose={() => setHoliday(false)}
          onSaved={async () => {
            setHoliday(false);
            await reload();
          }}
        />
      ) : null}
      {cat ? (
        <CategoryForm
          initial={cat === 'new' ? null : cat}
          onClose={() => setCat(null)}
          onSaved={async () => {
            setCat(null);
            await reloadCats();
          }}
        />
      ) : null}
    </>
  );
}

function TypeForm({ initial, onClose, onSaved }: { initial: LeaveType | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: initial?.name ?? '',
    paid: initial?.paid ?? true,
    limited: initial?.limited ?? true,
    default_days: String(initial?.default_days ?? 20),
    half_days: initial?.half_days ?? true,
    color: initial?.color ?? 0,
    active: initial?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const days = Number(f.default_days);
  const ok = f.name.trim().length >= 2 && (!f.limited || (Number.isFinite(days) && days >= 0 && days <= 366));
  return (
    <Dialog
      title={initial ? t('hr.edit_kind', { name: initial.name }) : t('hr.new_kind')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!ok) return;
              void run(async () => {
                await action('hr.save_type', { id: initial?.id ?? null, ...f, name: f.name.trim(), default_days: f.limited ? days : 0 });
                await onSaved();
              }, t('hr.saved'));
            }}
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('hr.kind_name')} error={tried && f.name.trim().length < 2 ? t('hr.e_name') : null} required>
          <input className="input" value={f.name} maxLength={60} placeholder={t('hr.kind_name_ph')} onChange={(e) => set('name', e.target.value)} data-autofocus />
        </Field>
        <div className="hr-colors" role="radiogroup" aria-label={t('hr.color')}>
          {TONES.map((c, i) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={f.color === i}
              aria-label={c}
              className={`hr-swatch ${tone(i)}${f.color === i ? ' selected' : ''}`}
              onClick={() => set('color', i)}
            />
          ))}
        </div>
        {(
          [
            ['paid', 'hr.is_paid', 'hr.is_paid_hint'],
            ['limited', 'hr.is_limited', 'hr.is_limited_hint'],
            ['half_days', 'hr.half_days', 'hr.half_days_hint'],
            ['active', 'hr.in_use', 'hr.in_use_hint'],
          ] as const
        ).map(([k, label, hint]) => (
          <div key={k} className="setting-row">
            <div>
              <strong>{t(label)}</strong>
              <p className="muted">{t(hint)}</p>
            </div>
            <Switch checked={f[k]} onChange={(v) => set(k, v)} label={t(label)} />
          </div>
        ))}
        {f.limited ? (
          <Field label={t('hr.usual_days')} hint={t('hr.usual_days_hint')} error={tried && !ok ? t('hr.e_days') : null}>
            <input className="input" type="number" min={0} max={366} step={0.5} value={f.default_days} onChange={(e) => set('default_days', e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}

function DaysDialog({ person, type, given, year, onClose, onSaved }: { person: Person; type: LeaveType; given: number | null; year: number; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [days, setDays] = useState(String(given ?? type.default_days));
  const [note, setNote] = useState('');
  const n = Number(days);
  return (
    <Dialog
      title={t('hr.set_days_for', { name: person.name, kind: type.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!Number.isFinite(n) || n < 0}
            onClick={() =>
              void run(async () => {
                await action('hr.allocate', { user: person.id, type: type.id, year, days: n, note: note.trim() || null });
                await onSaved();
              }, t('hr.saved'))
            }
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('hr.days_in', { year })}>
          <input className="input" type="number" min={0} max={366} step={0.5} value={days} onChange={(e) => setDays(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('hr.note')} hint={t('hr.days_note_hint')}>
          <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function GiveAllDialog({ type, year, onClose, onSaved }: { type: LeaveType; year: number; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [days, setDays] = useState(String(type.default_days));
  const { toast } = useUi();
  const n = Number(days);
  return (
    <Dialog
      title={t('hr.give_everyone', { kind: type.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!Number.isFinite(n) || n < 0}
            onClick={() =>
              void run(async () => {
                const r = await action<{ added: number }>('hr.allocate_all', { type: type.id, year, days: n });
                toast(tn('hr.given_to', r.added));
                await onSaved();
              })
            }
          >
            {t('hr.give')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('hr.give_everyone_hint', { year })}</p>
        <Field label={t('hr.days_in', { year })}>
          <input className="input" type="number" min={0} max={366} step={0.5} value={days} onChange={(e) => setDays(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function HolidayDialog({ year, onClose, onSaved }: { year: number; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [day, setDay] = useState(`${year}-${todayIso().slice(5)}`);
  const [name, setName] = useState('');
  const [tried, setTried] = useState(false);
  return (
    <Dialog
      title={t('hr.add_holiday')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (name.trim().length < 2 || !day) return;
              void run(async () => {
                await action('hr.save_holiday', { day, name: name.trim() });
                await onSaved();
              }, t('hr.holiday_added'));
            }}
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('hr.day')}>
          <input className="input" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        </Field>
        <Field label={t('hr.holiday_name')} error={tried && name.trim().length < 2 ? t('hr.e_name') : null} required>
          <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function CategoryForm({ initial, onClose, onSaved }: { initial: Category | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(initial?.name ?? '');
  const [limit, setLimit] = useState(initial?.limit ? plain(initial.limit) : '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [tried, setTried] = useState(false);
  const amount = limit.trim() ? parseMoney(limit) : 0;
  return (
    <Dialog
      title={initial ? t('hr.edit_category', { name: initial.name }) : t('hr.new_category')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (name.trim().length < 2 || amount === null) return;
              void run(async () => {
                await action('hr.save_category', { id: initial?.id ?? null, name: name.trim(), limit: amount, active });
                await onSaved();
              }, t('hr.saved'));
            }}
          >
            {t('hr.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('hr.category_name')} error={tried && name.trim().length < 2 ? t('hr.e_name') : null} required>
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('hr.limit')} hint={t('hr.limit_field_hint')} error={tried && amount === null ? t('hr.e_amount') : null}>
          <input className="input" inputMode="decimal" value={limit} placeholder="0.00" onChange={(e) => setLimit(e.target.value)} />
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('hr.in_use')}</strong>
            <p className="muted">{t('hr.category_in_use_hint')}</p>
          </div>
          <Switch checked={active} onChange={setActive} label={t('hr.in_use')} />
        </div>
      </div>
    </Dialog>
  );
}
